import type {
  BlockRole,
  LayoutRegion,
  PageLayout,
  PdfRect,
  TextBlock
} from "../types";

export interface RawTextItem {
  text: string;
  bbox: PdfRect;
  fontSize: number;
  order: number;
  hasEol?: boolean;
}

export interface TextLine {
  text: string;
  bbox: PdfRect;
  fontSize: number;
  order: number;
  regionId?: string;
  columnIndex?: number;
}

interface MutableLine {
  items: RawTextItem[];
  centerY: number;
}

function unionRect(a: PdfRect, b: PdfRect): PdfRect {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  const right = Math.max(a.x + a.width, b.x + b.width);
  const bottom = Math.max(a.y + a.height, b.y + b.height);
  return { x, y, width: right - x, height: bottom - y };
}

function unionRects(rects: PdfRect[]): PdfRect {
  return rects
    .slice(1)
    .reduce((result, rect) => unionRect(result, rect), rects[0]);
}

function median(values: number[]): number {
  if (!values.length) return 10;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[middle]
    : (sorted[middle - 1] + sorted[middle]) / 2;
}

function normalizeWhitespace(text: string): string {
  return text
    .replace(/\s+([,.;:!?%])/g, "$1")
    .replace(/([(\[]) +/g, "$1")
    .replace(/ +([)\]])/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
}

function shouldJoinWithoutSpace(previous: string, next: string): boolean {
  if (!previous || !next) return true;
  if (previous.endsWith("-") && /^[a-z]/.test(next)) return true;
  if (/[(\[/]$/.test(previous)) return true;
  if (/^[,.;:!?%)\]]/.test(next)) return true;
  return false;
}

function joinFragments(items: RawTextItem[]): string {
  let result = "";
  for (const item of items) {
    if (!item.text.trim()) continue;
    if (!result) {
      result = item.text;
    } else if (result.endsWith("-") && /^[a-z]/.test(item.text)) {
      result = result.slice(0, -1) + item.text;
    } else {
      result += shouldJoinWithoutSpace(result, item.text)
        ? item.text
        : ` ${item.text}`;
    }
  }
  return normalizeWhitespace(result);
}

function horizontalGap(line: MutableLine, item: RawTextItem): number {
  const left = Math.min(...line.items.map((entry) => entry.bbox.x));
  const right = Math.max(
    ...line.items.map((entry) => entry.bbox.x + entry.bbox.width)
  );
  const itemRight = item.bbox.x + item.bbox.width;
  if (item.bbox.x > right) return item.bbox.x - right;
  if (left > itemRight) return left - itemRight;
  return 0;
}

export function groupItemsIntoLines(items: RawTextItem[]): TextLine[] {
  const lines: MutableLine[] = [];
  const orderedItems = [...items].sort((left, right) => {
    const vertical = left.bbox.y - right.bbox.y;
    if (Math.abs(vertical) > Math.min(left.fontSize, right.fontSize) * 0.35) {
      return vertical;
    }
    return left.bbox.x - right.bbox.x;
  });

  for (const item of orderedItems) {
    if (!item.text.trim()) continue;
    const centerY = item.bbox.y + item.bbox.height / 2;
    const candidates = lines
      .map((line) => {
        const lineFont = median(line.items.map((entry) => entry.fontSize));
        const verticalDistance = Math.abs(line.centerY - centerY);
        const gap = horizontalGap(line, item);
        const largerFont = Math.max(lineFont, item.fontSize);
        const smallerFont = Math.min(lineFont, item.fontSize);
        const tolerance = Math.max(
          1.5,
          largerFont * (smallerFont / largerFont < 0.86 ? 0.62 : 0.38)
        );
        const maximumGap = Math.max(
          5,
          Math.min(lineFont, item.fontSize) * 1.15
        );
        return {
          line,
          accepted:
            verticalDistance <= tolerance && gap <= maximumGap,
          score: verticalDistance * 4 + gap
        };
      })
      .filter((candidate) => candidate.accepted)
      .sort((left, right) => left.score - right.score);

    const candidate = candidates[0]?.line;
    if (candidate) {
      candidate.items.push(item);
      candidate.centerY =
        candidate.items.reduce(
          (sum, entry) => sum + entry.bbox.y + entry.bbox.height / 2,
          0
        ) / candidate.items.length;
    } else {
      lines.push({ items: [item], centerY });
    }
  }

  return lines
    .map((line) => {
      const ordered = [...line.items].sort(
        (left, right) => left.bbox.x - right.bbox.x
      );
      return {
        text: joinFragments(ordered),
        bbox: unionRects(ordered.map((item) => item.bbox)),
        fontSize: median(ordered.map((item) => item.fontSize)),
        order: Math.min(...ordered.map((item) => item.order))
      };
    })
    .sort((left, right) => {
      const vertical = left.bbox.y - right.bbox.y;
      if (Math.abs(vertical) > Math.min(left.fontSize, right.fontSize) * 0.4) {
        return vertical;
      }
      return left.bbox.x - right.bbox.x;
    });
}

function lineCenter(line: TextLine): number {
  return line.bbox.x + line.bbox.width / 2;
}

function lineBottom(line: TextLine): number {
  return line.bbox.y + line.bbox.height;
}

function detectColumnSplit(
  lines: TextLine[],
  pageWidth: number,
  pageHeight: number
): number | null {
  const bodyLines = lines.filter(
    (line) =>
      line.text.length >= 8 &&
      line.bbox.y >= pageHeight * 0.05 &&
      lineBottom(line) <= pageHeight * 0.95 &&
      line.bbox.width <= pageWidth * 0.56
  );
  const left = bodyLines.filter(
    (line) =>
      lineCenter(line) < pageWidth * 0.47 &&
      line.bbox.x + line.bbox.width < pageWidth * 0.59
  );
  const right = bodyLines.filter(
    (line) =>
      lineCenter(line) > pageWidth * 0.53 && line.bbox.x > pageWidth * 0.41
  );

  if (left.length < 4 || right.length < 4) return null;

  const totalCharacters = bodyLines.reduce(
    (sum, line) => sum + line.text.length,
    0
  );
  const leftCharacters = left.reduce((sum, line) => sum + line.text.length, 0);
  const rightCharacters = right.reduce(
    (sum, line) => sum + line.text.length,
    0
  );
  if (
    leftCharacters < totalCharacters * 0.16 ||
    rightCharacters < totalCharacters * 0.16
  ) {
    return null;
  }

  const leftRight = median(
    left.map((line) => line.bbox.x + line.bbox.width)
  );
  const rightLeft = median(right.map((line) => line.bbox.x));
  const gutter = rightLeft - leftRight;
  if (gutter < Math.max(12, pageWidth * 0.022)) return null;

  const pairedBaselines = left.filter((leftLine) =>
    right.some(
      (rightLine) =>
        Math.abs(
          leftLine.bbox.y +
            leftLine.bbox.height / 2 -
            (rightLine.bbox.y + rightLine.bbox.height / 2)
        ) <= Math.max(leftLine.fontSize, rightLine.fontSize) * 0.55
    )
  ).length;
  if (pairedBaselines < 3) return null;

  return (leftRight + rightLeft) / 2;
}

function clusterFullWidthLines(lines: TextLine[]): TextLine[][] {
  const clusters: TextLine[][] = [];
  for (const line of [...lines].sort((a, b) => a.bbox.y - b.bbox.y)) {
    const cluster = clusters.at(-1);
    if (!cluster) {
      clusters.push([line]);
      continue;
    }
    const previousBottom = Math.max(...cluster.map(lineBottom));
    const maximumGap = Math.max(
      18,
      median(cluster.map((entry) => entry.fontSize)) * 3
    );
    if (line.bbox.y - previousBottom <= maximumGap) {
      cluster.push(line);
    } else {
      clusters.push([line]);
    }
  }
  return clusters;
}

function createRegion(
  pageNumber: number,
  kind: LayoutRegion["kind"],
  columnIndex: number,
  order: number,
  lines: TextLine[],
  suffix: string
): LayoutRegion {
  return {
    id: `p${pageNumber}-r${suffix}`,
    pageNumber,
    kind,
    columnIndex,
    order,
    bbox: unionRects(lines.map((line) => line.bbox))
  };
}

function assignRegions(
  lines: TextLine[],
  pageNumber: number,
  pageWidth: number,
  pageHeight: number
): { regions: LayoutRegion[]; lines: TextLine[] } {
  const split = detectColumnSplit(lines, pageWidth, pageHeight);
  if (split === null) {
    const region = createRegion(
      pageNumber,
      "full-width",
      0,
      0,
      lines,
      "full"
    );
    return {
      regions: [region],
      lines: lines.map((line) => ({
        ...line,
        regionId: region.id,
        columnIndex: 0
      }))
    };
  }

  const full: TextLine[] = [];
  const left: TextLine[] = [];
  const right: TextLine[] = [];
  const tentativeLeft = lines.filter((line) => lineCenter(line) < split);
  const tentativeRight = lines.filter((line) => lineCenter(line) >= split);
  const pairedColumnTop = Math.min(
    ...tentativeLeft.flatMap((leftLine) => {
      const paired = tentativeRight.some(
        (rightLine) =>
          Math.abs(
            leftLine.bbox.y +
              leftLine.bbox.height / 2 -
              (rightLine.bbox.y + rightLine.bbox.height / 2)
          ) <= Math.max(leftLine.fontSize, rightLine.fontSize) * 0.55
      );
      return paired ? [leftLine.bbox.y] : [];
    })
  );
  const typicalFont = median(lines.map((line) => line.fontSize));
  for (const line of lines) {
    const outsideBody =
      line.bbox.y < pageHeight * 0.075 || lineBottom(line) > pageHeight * 0.955;
    const isWide = line.bbox.width >= pageWidth * 0.6;
    const isBeforeColumns =
      Number.isFinite(pairedColumnTop) &&
      lineBottom(line) < pairedColumnTop - typicalFont * 0.55;
    if (outsideBody || isWide || isBeforeColumns) {
      full.push(line);
    } else if (lineCenter(line) < split) {
      left.push(line);
    } else {
      right.push(line);
    }
  }

  if (left.length < 2 || right.length < 2) {
    const region = createRegion(
      pageNumber,
      "full-width",
      0,
      0,
      lines,
      "full"
    );
    return {
      regions: [region],
      lines: lines.map((line) => ({
        ...line,
        regionId: region.id,
        columnIndex: 0
      }))
    };
  }

  const columnTop = Math.min(
    ...left.concat(right).map((line) => line.bbox.y)
  );
  const topThreshold =
    columnTop + Math.max(24, median(left.concat(right).map((line) => line.fontSize)) * 3);
  const fullClusters = clusterFullWidthLines(full);
  const topClusters = fullClusters.filter(
    (cluster) => Math.max(...cluster.map(lineBottom)) <= topThreshold
  );
  const bottomClusters = fullClusters.filter(
    (cluster) => !topClusters.includes(cluster)
  );

  const regions: LayoutRegion[] = [];
  const assigned = new Map<TextLine, Pick<TextLine, "regionId" | "columnIndex">>();
  let order = 0;
  topClusters.forEach((cluster, index) => {
    const region = createRegion(
      pageNumber,
      "full-width",
      0,
      order++,
      cluster,
      `top-${index + 1}`
    );
    regions.push(region);
    cluster.forEach((line) =>
      assigned.set(line, { regionId: region.id, columnIndex: 0 })
    );
  });

  const leftRegion = createRegion(
    pageNumber,
    "column",
    0,
    order++,
    left,
    "column-1"
  );
  const rightRegion = createRegion(
    pageNumber,
    "column",
    1,
    order++,
    right,
    "column-2"
  );
  regions.push(leftRegion, rightRegion);
  left.forEach((line) =>
    assigned.set(line, { regionId: leftRegion.id, columnIndex: 0 })
  );
  right.forEach((line) =>
    assigned.set(line, { regionId: rightRegion.id, columnIndex: 1 })
  );

  bottomClusters.forEach((cluster, index) => {
    const region = createRegion(
      pageNumber,
      "full-width",
      0,
      order++,
      cluster,
      `bottom-${index + 1}`
    );
    regions.push(region);
    cluster.forEach((line) =>
      assigned.set(line, { regionId: region.id, columnIndex: 0 })
    );
  });

  return {
    regions,
    lines: lines.map((line) => ({ ...line, ...assigned.get(line) }))
  };
}

function looksLikeHeading(text: string): boolean {
  return (
    /^(\d+(\.\d+)*|[IVX]+)\.?\s+\S/.test(text.trim()) ||
    /^(abstract|introduction|conclusion|references|related work)$/i.test(
      text.trim()
    )
  );
}

function looksLikeFormula(text: string): boolean {
  const cleaned = text.trim();
  const mathSymbols = (
    cleaned.match(/[=+×÷∑∫√≈≤≥<>^_{}[\]|│∼μσφθ]/g) ?? []
  ).length;
  return (
    (cleaned.length < 120 &&
      mathSymbols >= Math.max(2, Math.floor(cleaned.length * 0.12))) ||
    (cleaned.length < 48 &&
      /^(log|arg\s*max|arg\s*min|L\s*=|p\s*\(|q\s*\()/i.test(cleaned))
  );
}

function canMergeLines(
  previous: TextLine,
  next: TextLine,
  bodyFontSize: number
): boolean {
  if (
    previous.regionId !== next.regionId ||
    previous.columnIndex !== next.columnIndex
  ) {
    return false;
  }
  const font = Math.max(previous.fontSize, next.fontSize);
  const verticalProgress = next.bbox.y - previous.bbox.y;
  if (verticalProgress < -font * 0.2) return false;

  const gap = next.bbox.y - lineBottom(previous);
  if (gap > font * 1.08) return false;
  if (Math.abs(previous.fontSize - next.fontSize) > font * 0.2) return false;
  if (looksLikeHeading(previous.text)) return false;
  if (looksLikeHeading(next.text)) return false;
  if (looksLikeFormula(previous.text) || looksLikeFormula(next.text)) {
    return false;
  }

  const overlap =
    Math.min(
      previous.bbox.x + previous.bbox.width,
      next.bbox.x + next.bbox.width
    ) - Math.max(previous.bbox.x, next.bbox.x);
  const aligned =
    Math.abs(previous.bbox.x - next.bbox.x) <= font * 1.55 ||
    overlap > Math.min(previous.bbox.width, next.bbox.width) * 0.62;
  if (!aligned) return false;

  const previousEndsSentence = /[.!?]["')\]]?$/.test(previous.text.trim());
  const indentation = next.bbox.x - previous.bbox.x;
  if (previousEndsSentence && indentation > font * 1.25) return false;
  if (previousEndsSentence && gap > Math.max(2, bodyFontSize * 0.48)) {
    return false;
  }
  return true;
}

function classifyRole(
  text: string,
  rect: PdfRect,
  pageHeight: number,
  fontSize: number,
  bodyFontSize: number,
  inReferences: boolean
): BlockRole {
  const cleaned = text.trim();
  if (rect.y < pageHeight * 0.075 || rect.y + rect.height > pageHeight * 0.955) {
    return "header-footer";
  }
  if (inReferences) return "reference";
  if (/^(figure|fig\.|table)\s*\d+/i.test(cleaned)) return "caption";
  if (
    rect.y < pageHeight * 0.25 &&
    /(?:[A-Z][a-z]+\s+[A-Z][a-z]+\s*\d*\s*){2,}/.test(cleaned)
  ) {
    return "header-footer";
  }
  if (
    /^(\d+\.)+\s*\S/.test(cleaned) ||
    /^(abstract|introduction|conclusion|references|related work)$/i.test(
      cleaned
    )
  ) {
    return "heading";
  }
  if (
    fontSize <= bodyFontSize * 0.94 &&
    rect.y > pageHeight * 0.72
  ) {
    return "footnote";
  }
  if (
    fontSize <= bodyFontSize * 0.78 &&
    cleaned.length < 32
  ) {
    return "formula";
  }
  if (looksLikeFormula(cleaned)) {
    return "formula";
  }
  if (cleaned.length < 18 && rect.width < bodyFontSize * 6) {
    return "formula";
  }
  if (
    fontSize >= bodyFontSize * 1.55 ||
    (rect.y < pageHeight * 0.25 && fontSize >= bodyFontSize * 1.3)
  ) {
    return "title";
  }
  if (
    fontSize >= bodyFontSize * 1.12 ||
    looksLikeHeading(cleaned)
  ) {
    return "heading";
  }
  return "paragraph";
}

function isTranslatable(role: BlockRole, text: string): boolean {
  if (role !== "title" && role !== "heading" && role !== "paragraph") {
    return false;
  }
  const letters = (text.match(/[A-Za-z]/g) ?? []).length;
  return letters >= 4 && text.length >= 5;
}

function joinLines(lines: TextLine[]): string {
  let result = "";
  for (const line of lines) {
    if (result.endsWith("-") && /^[a-z]/.test(line.text)) {
      result = result.slice(0, -1) + line.text;
    } else {
      result += `${result ? " " : ""}${line.text}`;
    }
  }
  return normalizeWhitespace(result);
}

function blocksForRegion(
  lines: TextLine[],
  pageNumber: number,
  pageHeight: number,
  bodyFontSize: number
): Omit<TextBlock, "id" | "order">[] {
  const groups: TextLine[][] = [];
  const sorted = [...lines].sort(
    (left, right) =>
      left.bbox.y - right.bbox.y || left.bbox.x - right.bbox.x
  );
  for (const line of sorted) {
    const group = groups.at(-1);
    if (group && canMergeLines(group.at(-1)!, line, bodyFontSize)) {
      group.push(line);
    } else {
      groups.push([line]);
    }
  }

  let referencesStarted = false;
  return groups.map((group) => {
    const text = joinLines(group);
    const bbox = unionRects(group.map((line) => line.bbox));
    const fontSize = median(group.map((line) => line.fontSize));
    const startsReferences = /^references\b/i.test(text);
    const role = classifyRole(
      text,
      bbox,
      pageHeight,
      fontSize,
      bodyFontSize,
      referencesStarted || startsReferences
    );
    if (startsReferences) referencesStarted = true;
    return {
      pageNumber,
      regionId: group[0].regionId ?? `p${pageNumber}-rfull`,
      role,
      text,
      bbox,
      fontSize,
      translatable: isTranslatable(role, text)
    };
  });
}

export function groupLinesIntoBlocks(
  lines: TextLine[],
  pageNumber: number,
  pageHeight: number,
  regions?: LayoutRegion[]
): TextBlock[] {
  if (!lines.length) return [];
  const effectiveRegions =
    regions ??
    [
      createRegion(
        pageNumber,
        "full-width",
        0,
        0,
        lines,
        "full"
      )
    ];
  const defaultRegion = effectiveRegions[0];
  const normalizedLines = lines.map((line) => ({
    ...line,
    regionId: line.regionId ?? defaultRegion.id,
    columnIndex: line.columnIndex ?? defaultRegion.columnIndex
  }));
  const bodyFontSize = median(
    normalizedLines
      .filter(
        (line) =>
          line.text.length > 30 &&
          line.bbox.y >= pageHeight * 0.05 &&
          lineBottom(line) <= pageHeight * 0.95
      )
      .map((line) => line.fontSize)
  );

  const unordered = effectiveRegions.flatMap((region) =>
    blocksForRegion(
      normalizedLines.filter((line) => line.regionId === region.id),
      pageNumber,
      pageHeight,
      bodyFontSize
    ).map((block) => ({ block, regionOrder: region.order }))
  );

  return unordered
    .sort(
      (left, right) =>
        left.regionOrder - right.regionOrder ||
        left.block.bbox.y - right.block.bbox.y ||
        left.block.bbox.x - right.block.bbox.x
    )
    .map(({ block }, index) => ({
      ...block,
      id: `p${pageNumber}-b${index + 1}`,
      order: index
    }));
}

export function buildPageLayoutFromItems(
  items: RawTextItem[],
  pageNumber: number,
  pageWidth: number,
  pageHeight: number
): PageLayout {
  const provisionalLines = groupItemsIntoLines(items);
  if (!provisionalLines.length) {
    return {
      pageNumber,
      width: pageWidth,
      height: pageHeight,
      regions: [],
      blocks: []
    };
  }
  const assigned = assignRegions(
    provisionalLines,
    pageNumber,
    pageWidth,
    pageHeight
  );
  return {
    pageNumber,
    width: pageWidth,
    height: pageHeight,
    regions: assigned.regions,
    blocks: groupLinesIntoBlocks(
      assigned.lines,
      pageNumber,
      pageHeight,
      assigned.regions
    )
  };
}
